// React Query hooks for the holidays settings page.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  bulkBankHolidays,
  createHoliday,
  deleteHoliday,
  listHolidays,
  updateHolidayAction,
  type BulkBankHolidaysInput,
  type CreateHolidayInput,
  type HolidayAction,
  type HolidayListResponse,
  type ListHolidaysFilters,
} from '../../../../api/holidays';
import { queryKeys } from '../../../../api/queryKeys';
import { useToast } from '../../../../hooks/useToast';

export function useHolidays(filters: ListHolidaysFilters) {
  return useQuery<HolidayListResponse>({
    queryKey: queryKeys.holidays.list(filters as Record<string, unknown>),
    queryFn: () => listHolidays(filters),
    staleTime: 30_000,
  });
}

export function useCreateHoliday() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (input: CreateHolidayInput) => createHoliday(input),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: queryKeys.holidays.all });
      toast.success(`Holiday scheduled for ${res.holiday.entity_name}.`);
    },
    onError: () => {
      toast.error('Failed to schedule holiday.');
    },
  });
}

export function useUpdateHoliday() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: ({ uuid, action }: { uuid: string; action: HolidayAction }) =>
      updateHolidayAction(uuid, action),
    onSuccess: (_res, vars) => {
      void qc.invalidateQueries({ queryKey: queryKeys.holidays.all });
      toast.success(
        vars.action === 'cancel'
          ? 'Holiday cancelled.'
          : 'Holiday reinstated.',
      );
    },
    onError: () => {
      toast.error('Failed to update holiday.');
    },
  });
}

export function useDeleteHoliday() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (uuid: string) => deleteHoliday(uuid),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.holidays.all });
      toast.success('Holiday deleted.');
    },
    onError: () => {
      toast.error('Failed to delete holiday.');
    },
  });
}

export function useBulkBankHolidays() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (input: BulkBankHolidaysInput) => bulkBankHolidays(input),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: queryKeys.holidays.all });
      const msg =
        res.skipped > 0
          ? `${res.created} holidays created, ${res.skipped} already existed.`
          : `${res.created} holidays created.`;
      toast.success(msg);
    },
    onError: () => {
      toast.error('Failed to import bank holidays.');
    },
  });
}
